/**
 * The live progress lines of a running document analysis — what each step is working on,
 * with the model's own summary of how it thinks it through, as it writes it.
 *
 * The substrate sends them on the run's push channel as `analysis_progress` messages
 * (`workflows/analysis/progress.py`), a different `event_type` from the status pushes the
 * page's other listeners refetch on, so the shared instance hook hands them to their own
 * callback and never to `onEvent`. Nothing is stored: a page opened mid-run starts its
 * feed from then.
 *
 * A step copy sends a line when it starts, then a `thinking` line each time its summary
 * has grown (with the words so far, whole — never a fragment), then `done`.
 */

export const ANALYSIS_PROGRESS_EVENT = "analysis_progress";

/**
 * Where a step copy is: started, writing its thinking, done, ended without an answer, or
 * waiting out a busy model.
 */
export type ProgressPhase = "started" | "thinking" | "done" | "failed" | "waiting";

export interface AnalysisProgressEvent {
  instance_id: string;
  event_type: typeof ANALYSIS_PROGRESS_EVENT;
  /** One step copy's id: every line of the copy replaces the one before. */
  id: string;
  step_id: string;
  /** The step in words ("Compare wording"). */
  label: string;
  /** The section a per-section copy is on. */
  detail: string | null;
  phase: ProgressPhase;
  /** The model's summary of its thinking (so far, on a `thinking` line). */
  reasoning: string | null;
  timestamp: string;
  /** Milliseconds; orders a copy's lines when they arrive out of order or twice. */
  seq?: number;
}

export interface ProgressItem {
  id: string;
  label: string;
  detail: string | null;
  phase: ProgressPhase;
  reasoning: string | null;
  seq: number | null;
}

/** A run has a few dozen step copies; this is a guard, not a limit anyone reaches. */
export const MAX_PROGRESS_ITEMS = 200;

const PHASES: readonly ProgressPhase[] = ["started", "thinking", "done", "failed", "waiting"];

export function isProgressEvent(evt: unknown): evt is AnalysisProgressEvent {
  if (typeof evt !== "object" || evt === null) return false;
  const e = evt as Record<string, unknown>;
  return (
    e.event_type === ANALYSIS_PROGRESS_EVENT &&
    typeof e.id === "string" &&
    typeof e.label === "string" &&
    PHASES.includes(e.phase as ProgressPhase)
  );
}

const isFinished = (phase: ProgressPhase) => phase === "done" || phase === "failed";

/**
 * The feed after `evt`, in the order the copies began (like a chat: the first is at the
 * top, the newest at the bottom). A copy's line is replaced IN PLACE by its newest state,
 * so a copy's text grows where it started. A line older than what is shown (by `seq`), or
 * a start that arrives after a finish, changes nothing — the channel can deliver a message
 * twice or late; a copy that waited out a busy model and starts again does start again.
 * Returns a new list.
 */
export function applyProgress(
  items: readonly ProgressItem[],
  evt: AnalysisProgressEvent,
): ProgressItem[] {
  const at = items.findIndex((i) => i.id === evt.id);
  const existing = at >= 0 ? items[at] : undefined;
  if (existing) {
    if (evt.seq != null && existing.seq != null && evt.seq < existing.seq) return [...items];
    if (isFinished(existing.phase) && !isFinished(evt.phase)) return [...items];
  }
  const next: ProgressItem = {
    id: evt.id,
    label: evt.label,
    detail: evt.detail ?? null,
    phase: evt.phase,
    // A finish with no words of its own keeps the thinking that streamed in.
    reasoning:
      evt.reasoning ?? (existing?.phase === "thinking" && isFinished(evt.phase) ? existing.reasoning : null),
    seq: evt.seq ?? existing?.seq ?? null,
  };
  const list = existing ? items.map((i, n) => (n === at ? next : i)) : [...items, next];
  return list.slice(-MAX_PROGRESS_ITEMS);
}
